import React, { useState } from 'react'
import { MessageSquare, X, Sparkles, ChevronDown, ChevronUp } from 'lucide-react'
import { useAI } from '../../hooks/useAI'
import { useThemeColors } from '../../hooks/useThemeColors'

export default function AIFloatingButton() {
  const { colors } = useThemeColors()
  const { isOpen, setIsOpen, messages } = useAI()
  const [isCollapsed, setIsCollapsed] = useState(true)
  const hasUnread = messages.length > 0 && messages[messages.length - 1]?.role === 'assistant'
  const unreadCount = messages.filter(m => m.role === 'assistant').length

  // Panel mode - always visible at bottom-right
  if (!isOpen && !isCollapsed) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          backgroundColor: colors.backgroundLight,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          boxShadow: '0 8px 16px rgba(34, 211, 238, 0.12), 0 16px 32px rgba(34, 211, 238, 0.16)',
          zIndex: 999,
          overflow: 'hidden',
          transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            backgroundColor: colors.background,
            borderBottom: `1px solid ${colors.border}`,
            cursor: 'pointer'
          }}
          onClick={() => setIsCollapsed(true)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Sparkles size={18} style={{ color: colors.primary }} />
            <span style={{
              color: colors.text,
              fontSize: '14px',
              fontWeight: 600
            }}>
              AI Assistant
            </span>
            {hasUnread && (
              <div style={{
                backgroundColor: colors.error,
                color: colors.background,
                fontSize: '11px',
                fontWeight: 600,
                padding: '2px 6px',
                borderRadius: '10px',
                minWidth: '18px',
                textAlign: 'center'
              }}>
                {unreadCount}
              </div>
            )}
          </div>
          <ChevronDown size={16} style={{ color: colors.textSecondary }} />
        </div>

        {/* Quick status/info */}
        <div style={{ padding: '16px' }}>
          <p style={{
            color: colors.textSecondary,
            fontSize: '13px',
            marginBottom: '12px'
          }}>
            {hasUnread
              ? `You have ${unreadCount} unread ${unreadCount === 1 ? 'message' : 'messages'}`
              : 'Ready to help with bookings, classes, and more'}
          </p>
          <button
            onClick={() => setIsOpen(true)}
            style={{
              width: '100%',
              backgroundColor: colors.primary,
              color: colors.background,
              border: 'none',
              borderRadius: '8px',
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
              minHeight: '44px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryHover
              e.currentTarget.style.transform = 'scale(1.02)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.primary
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            <MessageSquare size={18} />
            Open Assistant
          </button>
        </div>
      </div>
    )
  }

  // Collapsed button - minimal presence
  if (isCollapsed) {
    return (
      <button
        onClick={() => setIsCollapsed(false)}
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          backgroundColor: colors.primary,
          color: colors.background,
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 4px 16px ${colors.borderLight}`,
          transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 999
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.05)'
          e.currentTarget.style.boxShadow = `0 6px 20px ${colors.primaryAlpha40}`
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)'
          e.currentTarget.style.boxShadow = `0 4px 16px ${colors.borderLight}`
        }}
      >
        <Sparkles size={24} />
        {hasUnread && (
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: colors.error,
            border: `2px solid ${colors.background}`
          }} />
        )}
      </button>
    )
  }

  // When assistant is open, show close button
  return (
    <button
      onClick={() => setIsOpen(false)}
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        width: '56px',
        height: '56px',
        borderRadius: '50%',
        backgroundColor: colors.primary,
        color: colors.background,
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: `0 4px 16px ${colors.borderLight}`,
        transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: 999
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.05)'
        e.currentTarget.style.boxShadow = `0 6px 20px ${colors.primaryAlpha40}`
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)'
        e.currentTarget.style.boxShadow = `0 4px 16px ${colors.borderLight}`
      }}
    >
      <X size={24} />
    </button>
  )
}
